import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;
let pendingPageView: { name: string; uri?: string } | null = null;
let initializationPromise: Promise<void> | null = null;

interface AppInsightsConfig {
	connectionString: string;
}

const EVO_ORGANISATION_ID_KEY = 'evo_organisation_id';
const WORKSPACE_ORGANISATION_ID_KEY = 'workspace_organisation_id';
const PLATFORM_USER_ID_KEY = 'platform_user_id';

const toHex = (bytes: Uint8Array): string =>
	Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

// Generates a random W3C TraceContext id (hex string) of the given byte length.
const generateHexId = (byteLength: number): string => {
	const bytes = new Uint8Array(byteLength);
	crypto.getRandomValues(bytes);
	return toHex(bytes);
};

// Starts a fresh distributed-trace operation so telemetry raised after this
// point (the page view and any subsequent ajax/fetch dependency calls) is
// grouped under a new operation id rather than inheriting the previous one.
const startNewOperation = (): void => {
	const traceCtx = appInsights?.getTraceCtx();
	if (traceCtx) {
		traceCtx.traceId = generateHexId(16); // 32 hex chars
		traceCtx.spanId = generateHexId(8); // 16 hex chars
	}
};

const initializeWithConnectionString = (connectionString: string): void => {
	if (!connectionString) {
		return;
	}

	if (appInsights) {
		return;
	}

	appInsights = new ApplicationInsights({
		config: {
			connectionString,
			enableAutoRouteTracking: false,
			disableFetchTracking: false,
			disableAjaxTracking: false,
			enableCorsCorrelation: true,
			enableRequestHeaderTracking: true,
			enableResponseHeaderTracking: true,
		},
	});

	appInsights.loadAppInsights();

	// Add telemetry initializer to automatically include custom attributes in all telemetry
	appInsights.addTelemetryInitializer((envelope) => {
		const organisationId = sessionStorage.getItem(EVO_ORGANISATION_ID_KEY);
		const workspaceOrganisationId = sessionStorage.getItem(WORKSPACE_ORGANISATION_ID_KEY);
		const userId = sessionStorage.getItem(PLATFORM_USER_ID_KEY);

		envelope.data = envelope.data || {};

		// Static attributes
		envelope.data['application.name'] = 'Time Track Pro';
		envelope.data['subsystem.name'] = 'Time Track Pro';
		envelope.data['pe.code'] = '199';

		// Dynamic attributes from session context
		if (organisationId) {
			envelope.data.OrganisationId = organisationId;
		}
		// WorkspaceOrganisationId is the platform/evo workspace OrganisationId
		// (GUID from the X-Organisation-Id header), never the integer database id.
		if (workspaceOrganisationId) {
			envelope.data.WorkspaceOrganisationId = workspaceOrganisationId;
		}
		if (userId) {
			envelope.data.UserId = userId;
			envelope.data.WorkspaceUserId = userId;
		}
	});

	if (pendingPageView) {
		appInsights.trackPageView({
			name: pendingPageView.name,
			uri: pendingPageView.uri ?? window.location.href,
		});
		pendingPageView = null;
	}
};

export const initializeAppInsightsFromConfig = (): Promise<void> => {
	if (initializationPromise) {
		return initializationPromise;
	}

	initializationPromise = (async () => {
		try {
			const response = await fetch('/config/appInsights.config.json');
			if (!response.ok) {
				return;
			}
			const config: AppInsightsConfig = await response.json();
			if (config.connectionString) {
				initializeWithConnectionString(config.connectionString);
			}
		} catch {
			// Config file not found or invalid - silently skip initialization
		}
	})();

	return initializationPromise;
};

export const getAppInsights = (): ApplicationInsights | null => {
	return appInsights;
};

export const trackPageView = (name: string, uri?: string): void => {
	// Custom properties (OrganisationId, UserId) are automatically added
	// by the telemetry initializer when the SDK is initialized
	if (appInsights) {
		// Start a new operation per route change so each navigation (and its
		// subsequent dependency calls) gets a distinct operation id.
		startNewOperation();
		appInsights.trackPageView({
			name,
			uri: uri ?? window.location.href,
		});
	} else {
		pendingPageView = { name, uri };
	}
};
